import type { LucideIcon } from 'lucide-react';

/** Sentinel value used by the filter selects to represent "no filter applied" (show all). */
export const FILTER_ALL_VALUE = '__all__';

export interface FilterOption {
  value: string;
  label: string;
  /**
   * How many list items match this option. When provided, shown as a trailing
   * number in the picker; rows with count 0 are rendered at reduced opacity.
   */
  count?: number;
}

/**
 * One facet a list can be narrowed by, described as data rather than as markup.
 *
 * {@link FilterBar} renders applied facets as chips and offers the rest through
 * a searchable "Add filter" menu, so it has to know every facet up front —
 * including the ones nobody has touched yet. A children-based API could only
 * show what the caller chose to mount, which is exactly the set the menu needs
 * to look beyond.
 */
export interface FilterDefinition {
  /** Stable id. React key, and the suffix of the chip's test id. */
  id: string;
  /** Facet name shown on the chip and in the add-filter list, e.g. "Status". */
  label: string;
  /** Leading glyph. Gives the chip and the menu row a shape to recognize. */
  icon?: LucideIcon;
  /** Current value. {@link FILTER_ALL_VALUE} means the facet is not applied. */
  value: string;
  /**
   * Selectable values. A {@link FILTER_ALL_VALUE} entry is ignored: chips are
   * removed with their own control, so an "All" row would be a second, weaker
   * way to do the same thing.
   */
  options: FilterOption[];
  /** Receives the picked value, or {@link FILTER_ALL_VALUE} when cleared. */
  onValueChange: (value: string) => void;
  /** Placeholder for the value search box. Defaults to `Search {label}…`. */
  searchPlaceholder?: string;
  /**
   * When true, a search input is shown above the option list. Use for facets
   * with many options (owner, repo, tags) where scanning is impractical.
   */
  withSearch?: boolean;
  /**
   * When true, renders as a pill toggle at the top of the filter popover instead
   * of an entry in the category list. Use for single-option boolean facets (e.g.
   * Unread, Drafts) where the cost of a two-step picker is higher than the value.
   */
  quickToggle?: boolean;
}

/** Whether the facet is currently narrowing the list. */
export function isFilterApplied(definition: FilterDefinition): boolean {
  return definition.value !== FILTER_ALL_VALUE;
}

/**
 * What the chip shows for the current value. Falls back to the raw value so a
 * filter pinned to something no longer in `options` (a repo with no runs left
 * in view, say) still reads as itself instead of going blank.
 */
export function filterValueLabel(definition: FilterDefinition): string {
  return (
    definition.options.find((option) => option.value === definition.value)?.label ??
    definition.value
  );
}

/** Selectable rows for a facet: everything but the "all" sentinel. */
export function selectableOptions(definition: FilterDefinition): FilterOption[] {
  return definition.options.filter((option) => option.value !== FILTER_ALL_VALUE);
}
